import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripeService } from '../../services/stripeService';

interface StripePaymentFormProps {
  amount: number;
  orderId: string;
  customerEmail: string;
  customerName: string;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '12px',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false,
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  amount,
  orderId,
  customerEmail,
  customerName,
  onPaymentSuccess,
  onPaymentError,
  isProcessing,
  setIsProcessing
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string>('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onPaymentError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onPaymentError('Card element not found. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);
    setCardError('');

    try {
      // Create payment intent. Throws on any backend/auth/network failure —
      // we no longer fall through to a "simulate payment" bypass. The user
      // sees the real error and either retries or contacts support.
      const paymentIntentResult = await stripeService.createPaymentIntent({
        amount: Math.round(amount * 100), // Convert to cents
        currency: 'usd',
        orderId,
        customerEmail,
        description: `Order ${orderId}`,
      });

      // Confirm payment with Stripe. NEW global flow: backend returns a
      // PaymentIntent client_secret for the first-period real charge
      // (works for US, EU, IN, SG, JP, AU, BR — handles India RBI mandate
      // automatically via setup_future_usage). We confirm it once, 3DS
      // appears if the issuer requires it, money is charged, then backend
      // creates the subscription with billing_cycle_anchor in the future
      // so there's no double-charge.
      {
        // `requiresSetup` is now true for the recurring-services branch in
        // both old SetupIntent flow and new PaymentIntent flow.
        // `isSubscriptionPayment` (new) tells us to use confirmCardPayment.
        if (paymentIntentResult.requiresSetup || paymentIntentResult.isSubscriptionSetup) {
          const isNewFlow = paymentIntentResult.isSubscriptionPayment === true;
          const token = localStorage.getItem('token');

          if (isNewFlow) {
            // NEW: confirm the real PaymentIntent for the first period.
            console.log('💳 Confirming PaymentIntent (new global flow)...');
            const { error, paymentIntent } = await stripe.confirmCardPayment(
              paymentIntentResult.clientSecret!,
              {
                payment_method: {
                  card: cardElement,
                  billing_details: {
                    name: customerName,
                    email: customerEmail,
                  },
                },
              }
            );

            if (error) {
              console.error('❌ PaymentIntent confirmation error:', error);
              throw new Error(error.message || 'Payment failed');
            }

            if (paymentIntent?.status !== 'succeeded') {
              console.error('❌ PaymentIntent ended in non-succeeded state:', paymentIntent?.status);
              throw new Error(`Payment did not complete (status: ${paymentIntent?.status})`);
            }

            console.log('✅ PaymentIntent succeeded, creating subscription...');
            // Create subscription against the now-saved payment method.
            const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/payments/create-subscription-after-setup`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                orderId: orderId,
                paymentIntentId: paymentIntent.id,
                paymentMethodId: paymentIntent.payment_method,
              }),
            });
            const subscriptionResult = await subscriptionResponse.json();
            if (!subscriptionResult.success) {
              console.error('❌ Subscription creation failed:', subscriptionResult.error);
              throw new Error(subscriptionResult.error || 'Failed to create subscription');
            }

            // Complete the order (registers domain, provisions VPS, etc.)
            try {
              await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/payments/complete-order`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  orderId: orderId,
                  subscriptionId: subscriptionResult.data.subscriptionId,
                }),
              });
            } catch (completeError) {
              console.error('❌ Failed to complete order:', completeError);
            }

            onPaymentSuccess(subscriptionResult.data.subscriptionId);
            return;
          }

          // LEGACY SetupIntent flow (kept for back-compat with cached frontends).
          const { error, setupIntent } = await stripe.confirmCardSetup(
            paymentIntentResult.clientSecret!,
            {
              payment_method: {
                card: cardElement,
                billing_details: {
                  name: customerName,
                  email: customerEmail,
                },
              },
            }
          );

          if (error) {
            console.error('❌ SetupIntent confirmation error:', error);
            console.error('❌ Error details:', JSON.stringify(error, null, 2));
            throw new Error(error.message || 'SetupIntent confirmation failed');
          }

          if (setupIntent?.status === 'succeeded') {

            // Create subscription with the saved payment method
            const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/payments/create-subscription-after-setup`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                orderId: orderId,
                setupIntentId: setupIntent.id,
                paymentMethodId: setupIntent.payment_method
              })
            });

            const subscriptionResult = await subscriptionResponse.json();

            if (!subscriptionResult.success) {
              console.error('❌ Subscription creation failed:', subscriptionResult.error);
              throw new Error(subscriptionResult.error || 'Failed to create subscription');
            }


            // Check if subscription requires additional 3D Secure authentication
            if (subscriptionResult.data.requiresAction && subscriptionResult.data.clientSecret) {

              const { error: actionError, paymentIntent: actionPaymentIntent } = await stripe.confirmPayment({
                clientSecret: subscriptionResult.data.clientSecret,
                confirmParams: {
                  return_url: `${window.location.origin}/payment-success`,
                  payment_method: setupIntent.payment_method as string
                },
                redirect: 'if_required'
              });

              if (actionError) {
                throw new Error(actionError.message || '3D Secure authentication failed');
              }

              if (actionPaymentIntent?.status === 'succeeded') {

                // Complete the order
                try {
                  const completeResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/payments/complete-order`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      orderId: orderId,
                      subscriptionId: subscriptionResult.data.subscriptionId
                    })
                  });

                  const completeResult = await completeResponse.json();
                  if (completeResult.success) {
                  }
                } catch (completeError) {
                  console.error('❌ Failed to complete order:', completeError);
                }

                onPaymentSuccess(subscriptionResult.data.subscriptionId);
              } else {
                throw new Error('3D Secure authentication was not successful');
              }
            } else {
              console.log('✅ Subscription created without additional authentication needed');
              
              // Complete the order
              try {
                const completeResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/payments/complete-order`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    orderId: orderId,
                    subscriptionId: subscriptionResult.data.subscriptionId
                  })
                });

                const completeResult = await completeResponse.json();
                if (completeResult.success) {
                }
              } catch (completeError) {
                console.error('❌ Failed to complete order:', completeError);
              }
              
              onPaymentSuccess(subscriptionResult.data.subscriptionId);
            }
          } else {
            throw new Error('SetupIntent confirmation was not successful');
          }
        } else {
          console.log('🔧 Using PaymentIntent confirmation for immediate payment...');
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            paymentIntentResult.clientSecret!,
            {
              payment_method: {
                card: cardElement,
                billing_details: {
                  name: customerName,
                  email: customerEmail,
                },
              },
            }
          );
          
          if (error) {
            console.error('❌ PaymentIntent confirmation error:', error);
            throw new Error(error.message || 'Payment confirmation failed');
          }

          if (paymentIntent?.status === 'succeeded') {
            console.log('✅ Payment succeeded:', paymentIntent.id);
            
            // Call backend to complete order and send emails
            try {
              console.log('📧 Completing order and sending confirmation emails...');
              const token = localStorage.getItem('token');
              const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/payments/complete-order`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  orderId: orderId,
                  paymentIntentId: paymentIntent.id
                })
              });

              const result = await response.json();
              if (result.success) {
                console.log('✅ Order completed and emails sent:', result.message);
              } else {
                console.warn('⚠️ Order completion failed:', result.error);
              }
            } catch (emailError) {
              console.error('❌ Failed to complete order:', emailError);
              // Don't fail the payment process if email sending fails
            }
            
            onPaymentSuccess(paymentIntent.id);
          } else {
            throw new Error('Payment was not successful');
          }
        }
      }

    } catch (error: any) {
      onPaymentError(error.message || 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event: any) => {
    if (event.error) {
      setCardError(event.error.message);
    } else {
      setCardError('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Secure Payment Notice */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-green-800">Secure Payment</p>
            <p className="text-xs text-green-700">
              Your payment is processed securely through Stripe. Your card information is encrypted and never stored on our servers.
            </p>
          </div>
        </div>
      </div>

      {/* Card Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="border border-gray-300 rounded-md p-3 bg-white">
          <CardElement
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        {cardError && (
          <p className="mt-2 text-sm text-red-600">{cardError}</p>
        )}
      </div>

      {/* Security Notice */}
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span>Your payment information is secured with 256-bit SSL encryption</span>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full px-6 py-3 bg-brand-orange text-white rounded-md hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold text-lg"
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing Payment...
          </div>
        ) : (
          `Pay $${amount.toFixed(2)}`
        )}
      </button>
    </form>
  );
};

export default StripePaymentForm;